import React, { useEffect, useState, useMemo } from "react";
import { Button, Col, Container, Modal, Navbar, Row } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { DndProvider, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import * as XLSX from "xlsx";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";

import api from "../../components/api";
import { Guest, Table, GuestRelationshipType } from "../../api";
import NavBar from "../../components/NavBar/NavBar";
import DraggableGuest from "../../components/DraggableGuest/DraggableGuest";
import DroppableTable from "../../components/DroppableTable/DroppableTable";
import ContextMenu from "../../components/ContextMenu/ContextMenu";

import "./WeddingTableListPage.css";
import roundTable from "../../icons/round_table.svg";
import longTable from "../../icons/long_table.svg";
import stage from "../../icons/stage.svg";
import circle from "../../icons/circle.svg";
import curvedTable from "../../icons/curved_table.svg";
import danceFloor from "../../icons/dance_floor.svg";
import sShapedTable from "../../icons/S_shaped_table.svg";
import { relationshipOptions } from "../../constants/relationshipTypes";

const tableStyleMap = {
  1: roundTable,
  2: longTable,
  3: stage,
  4: circle,
  5: curvedTable,
  6: danceFloor,
  7: sShapedTable,
} as const;

const styleMap = {
  "Round Table": 1,
  "Square Table": 2,
  "Stage": 3,
  "Circle": 4,
  "Curved Table": 5,
  "Dance Floor": 6,
  "S Shaped Table": 7,
} as const;

const TABLE_GAP = 0; // 桌子之间的间距（像素）
const TABLES_PER_COLUMN = 8; // 每列放置的桌子数量

function useOnClickOutside(
  ref: React.RefObject<HTMLElement>,
  handler: () => void,
) {
  useEffect(() => {
    const listener = (event: MouseEvent) => {
      if (!ref.current || ref.current.contains(event.target as Node)) {
        return;
      }
      handler();
    };

    document.addEventListener("mousedown", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
    };
  }, [ref, handler]);
}

interface TableDimensions {
  width: number;
  height: number;
}

const GRID_SIZE = 20; // 网格在屏幕上的像素大小
const REAL_GRID_SIZE = 50; // 每个网格代表的实际厘米数

// const calculateScale = () => {
//   return GRID_SIZE / REAL_GRID_SIZE; // 每厘米对应的像素数
// };

const updateSeatingChartStyle = async (
  weddingTableId: string,
  tableId: string,
  action: "add" | "delete",
) => {
  try {
    const weddingResponse =
      await api.weddings.getWeddingWeddingTableWeddingTableIdGet(weddingTableId);
    const currentStyle = weddingResponse.data.seating_chart_style
      ? JSON.parse(weddingResponse.data.seating_chart_style)
      : { tableOrder: [] };

    let updatedTableOrder;
    if (action === "add") {
      updatedTableOrder = [...(currentStyle.tableOrder || []), tableId];
    } else {
      updatedTableOrder = (currentStyle.tableOrder || []).filter(
        (id: string) => id !== tableId,
      );
    }

    const updatedStyle = {
      ...currentStyle,
      tableOrder: updatedTableOrder,
    };

    // 保存更新后的样式
    await api.weddings.patchWeddingsWeddingTableIdWeddingTableWeddingTableIdPatch(weddingTableId, {
      seating_chart_style: JSON.stringify(updatedStyle),
    });
  } catch (error) {
    console.error("Update seat failed:", error);
  }
};

function WeddingTableListPage() {
  const navigate = useNavigate();
  const { weddingTableId } = useParams<{ weddingTableId: string }>();

  const [tables, setTables] = useState<Table[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [showGuestCreate, setShowGuestCreate] = useState<boolean>(false);
  const [newGuestName, setNewGuestName] = useState<string>();
  const [newGuestCount, setNewGuestCount] = useState<number>();
  const [modifyGuestID, setModifyGuestID] = useState<string>();
  const [showTableCreate, setShowTableCreate] = useState<boolean>(false);
  const [newTableName, setNewTableName] = useState<string>();
  const [newTableCount, setNewTableCount] = useState<number>();
  const [newTableStyle, setNewTableStyle] = useState<string>();
  const [selectedTableStyle, setSelectedTableStyle] = useState<number>(1);
  const [modifyTableID, setModifyTableID] = useState<string>();
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    items: { label: string; onClick: () => void }[];
  }>();
  const [showQRCode, setShowQRCode] = useState(false);
  const [tableDimensions, setTableDimensions] = useState<TableDimensions>({
    width: 200,
    height: 200,
  });
  const [open, setOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [tableRotation, setTableRotation] = useState<number>(0);
  const [guestSortOrder, setGuestSortOrder] = useState<"asc" | "desc" | "none">(
    "none",
  );
  const [tableSortOrder, setTableSortOrder] = useState<"asc" | "desc" | "none">(
    "none",
  );
  const [guestRelationship, setGuestRelationship] = useState<string>("");
  const [showValidationError, setShowValidationError] = useState(false);
  const [selectedRelationship, setSelectedRelationship] =
    useState<string>("all");
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateGuests, setDuplicateGuests] = useState<Guest[]>([]);
  const [showInvitationLinkModal, setShowInvitationLinkModal] = useState(false);
  const [invitationLink, setInvitationLink] = useState("");
  const [isScrapingGuests, setIsScrapingGuests] = useState(false);
  const [hasExistingLink, setHasExistingLink] = useState(false);
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [weddingTable, setWeddingTable] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedTableForGuest, setSelectedTableForGuest] = useState<string>("");

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleAddTableClick = () => {
    setModifyTableID(undefined);
    setNewTableName("");
    setNewTableCount(undefined);
    setSelectedTableStyle(1);
    setShowTableCreate(true);
  };

  useEffect(() => {
    if (weddingTableId) {
      api.tables
        .getWeddingsWeddingTableIdTablesWeddingTableWeddingTableIdTablesGet(weddingTableId)
        .then((data) => {
          setTables(data.data.contents);
        });
      api.guests
        .getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(weddingTableId)
        .then((data) => {
          setGuests(
            data.data.contents.filter((guest) => {
              return !guest.table;
            }),
          );
        });
      api.weddings.getWeddingWeddingTableWeddingTableIdGet(weddingTableId)
        .then((data) => {
          setWeddingTable(data.data);
          
          // 检查是否已有电子请帖链接
          if (data.data.extras && data.data.extras.invitation_link) {
            setInvitationLink(data.data.extras.invitation_link);
            setHasExistingLink(true);
            setIsEditingLink(false);
          } else {
            setHasExistingLink(false);
            setIsEditingLink(true); // 如果没有链接，允许直接编辑
          }
        });
    }
  }, [weddingTableId]);

  const handleGuestAddToTable = (guestID: string, tableID: string) => {
    api.guests
      .patchGuestsGuestIdGuestsGuestIdPatch(guestID, {
        table_id: tableID,
      })
      .then((data) => {
        setGuests(
          guests.filter((guest) => {
            return guest.guest_id !== guestID;
          }),
        );
        const updatedTables = tables.map((table) => {
          table.guests = table.guests?.filter((guest) => {
            return (guest as Guest).guest_id !== data.data.guest_id;
          });
          if (table.table_id === tableID) {
            return {
              ...table,
              guests: [...(table.guests || []), data.data],
            };
          }
          return table;
        });
        setTables(updatedTables);
      });
  };

  const handleRemoveTableGuest = (guestID: string, tableID: string) => {
    api.guests
      .patchGuestsGuestIdGuestsGuestIdPatch(guestID, {
        reset_table: true,
      })
      .then((data) => {
        setGuests([...guests, data.data]);
        const updatedTables = tables.map((table) => {
          if (table.table_id === tableID) {
            return {
              ...table,
              guests: table.guests?.filter((guest) => {
                return (guest as Guest).guest_id !== guestID;
              }),
            };
          }
          return table;
        });
        setTables(updatedTables);
      });
  };

  // Modal operations
  const handleGuestCreate = () => {
    // 重置验证状态
    setShowValidationError(false);

    // 表单验证
    if (!newGuestName) {
      alert("宾客姓名不能为空");
      return;
    }

    if (!guestRelationship) {
      setShowValidationError(true);
      return;
    }

    // 检查是否有重名宾客（仅在创建新宾客，而非修改时检查）
    if (!modifyGuestID && weddingTableId) {
      // 收集所有已分配到桌子的宾客
      const assignedGuests: Guest[] = [];
      tables.forEach(table => {
        if (table.guests && Array.isArray(table.guests)) {
          table.guests.forEach(guest => {
            if (typeof guest === 'object' && guest !== null) {
              assignedGuests.push(guest as Guest);
            }
          });
        }
      });
      
      // 合并未分配和已分配的宾客，然后查找重名
      const allGuests = [...guests, ...assignedGuests];
      const existingGuests = allGuests.filter(
        (guest) => guest.guest_name === newGuestName
      );
      
      if (existingGuests.length > 0) {
        // 找到重名宾客，显示确认对话框
        setDuplicateGuests(existingGuests);
        setShowDuplicateConfirm(true);
        return; // 先返回，等用户确认后再继续
      }
    }

    if (weddingTableId) {
      const createOrUpdateGuest = async () => {
        try {
          // 添加类型注解以避免隐式any类型
          let response: { data: Guest };
          // 修改宾客信息
          if (modifyGuestID) {
            response = await api.guests.patchGuestsGuestIdGuestsGuestIdPatch(modifyGuestID, {
              guest_name: newGuestName,
              guest_count: newGuestCount,
              relationship_type: guestRelationship as GuestRelationshipType,
            });
            
            // 更新本地宾客列表
            const updatedGuests = guests.map((guest) => {
              if (guest.guest_id === modifyGuestID) {
                return {
                  ...guest,
                  guest_name: newGuestName,
                  guest_count: newGuestCount,
                  relationship_type: guestRelationship as GuestRelationshipType,
                };
              }
              return guest;
            });
            setGuests(updatedGuests);
          } 
          // 创建新宾客
          else {
            response = await api.guests.postWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsPost(weddingTableId, {
              guest_name: newGuestName,
              guest_count: newGuestCount,
              relationship_type: guestRelationship as GuestRelationshipType,
            });
            
            // 将新宾客添加到宾客列表
            setGuests([...guests, response.data]);
          }

          // 如果在移动端选择了桌子，分配宾客到桌子
          if (isMobile && selectedTableForGuest) {
            await api.guests.patchGuestsGuestIdGuestsGuestIdPatch(
              response.data.guest_id, 
              { table_id: selectedTableForGuest }
            );

            // 更新表格数据
            setGuests(prev => prev.filter(g => g.guest_id !== response.data.guest_id));
            
            setTables(prev => prev.map(table => {
              if (table.table_id === selectedTableForGuest) {
                return {
                  ...table,
                  guests: [...(table.guests || []), response.data]
                };
              }
              return table;
            }));
          }
          
          handleCancelModal();
        } catch (error) {
          console.error("宾客操作失败:", error);
          alert("操作失败，请重试");
        }
      };

      createOrUpdateGuest();
    }
  };

  const renderDimensionInputs = () => {
    return (
      <div className="dimension-input-container">
        <label>
          Width (cm):
          <input
            type="number"
            value={tableDimensions.width}
            onChange={(e) =>
              setTableDimensions({
                ...tableDimensions,
                width: Number(e.target.value),
              })
            }
          />
        </label>
        <label>
          Height (cm):
          <input
            type="number"
            value={tableDimensions.height}
            onChange={(e) =>
              setTableDimensions({
                ...tableDimensions,
                height: Number(e.target.value),
              })
            }
          />
        </label>
      </div>
    );
  };

  const renderRotationInput = () => {
    return (
      <div className="dimension-input-container">
        <label>
          Rotation Angle (°):
          <input
            type="number"
            value={tableRotation}
            onChange={(e) =>
              setTableRotation(Number(e.target.value))
            }
            min="0"
            max="360"
          />
        </label>
      </div>
    );
  };

  const handleTableCreate = () => {
    if (weddingTableId && newTableName) {
      const tableStyle = {
        id: "",
        name: newTableName,
        x: 0,
        y: 0,
        width: tableDimensions.width,
        height: tableDimensions.height,
        rotation: tableRotation,
        imageUrl: tableStyleMap[selectedTableStyle as keyof typeof tableStyleMap],
      };

      if (modifyTableID) {
        api.tables
          .patchTablesTableIdTablesTableIdPatch(modifyTableID, {
            table_name: newTableName,
            table_guest_count: newTableCount,
            table_style: JSON.stringify({
              ...JSON.parse(newTableStyle || "{}"),
              name: newTableName,
              ...tableDimensions,
              rotation: tableRotation,
              imageUrl: tableStyleMap[selectedTableStyle as keyof typeof tableStyleMap],
            }),
          })
          .then((data) => {
            const updatedTables = tables.map((table) => {
              if (table.table_id === modifyTableID) {
                return {
                  ...table,
                  table_name: newTableName,
                  table_guest_count: newTableCount,
                  table_style: JSON.stringify({
                    id: table.table_id,
                    name: newTableName,
                    x: 0,
                    y: 0,
                    ...tableDimensions,
                    rotation: tableRotation,
                    imageUrl: tableStyleMap[selectedTableStyle as keyof typeof tableStyleMap],
                  }),
                };
              }
              return table;
            });
            setTables(updatedTables);
            handleCancelModal();
            if (weddingTableId) {
              updateSeatingChartStyle(weddingTableId, modifyTableID, "delete");
            }
          });
      } else {
        api.tables
          .postTablesWeddingTableWeddingTableIdTablesPost(weddingTableId, {
            table_name: newTableName,
            table_guest_count: newTableCount,
            table_style: JSON.stringify({
              id: "",
              name: newTableName,
              x: 0,
              y: 0,
              ...tableDimensions,
              rotation: tableRotation,
              imageUrl: tableStyleMap[selectedTableStyle as keyof typeof tableStyleMap],
            }),
          })
          .then((data) => {
            setTables([...tables, data.data]);
            handleCancelModal();
            if (weddingTableId) {
              updateSeatingChartStyle(weddingTableId, data.data.table_id, "add");
            }
          });
      }
    }
  };

  const handleCancelModal = () => {
    setShowGuestCreate(false);
    setShowTableCreate(false);
    setNewGuestName(undefined);
    setNewGuestCount(undefined);
    setGuestRelationship(""); // 添加重置关系类型状态
    setModifyGuestID(undefined);
    setNewTableName(undefined);
    setNewTableCount(undefined);
    setNewTableStyle(undefined);
    setSelectedTableForGuest(""); // 重置选择的桌子
    setTableRotation(0);
    setTableDimensions({
      width: 200,
      height: 200,
    });
  };

  // Menu operations
  const handleGuestContextMenu = (e: React.MouseEvent, guest: Guest) => {
    e.preventDefault();

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: "Modify",
          onClick: () => {
            setModifyGuestID(guest.guest_id);
            setNewGuestName(guest.guest_name);
            setNewGuestCount(guest.guest_count);
            setGuestRelationship(guest.relationship_type || "");
            
            // 设置当前宾客的桌子ID（如果有）
            if (guest.table) {
              const tableId = guest.table.split('/').pop();
              setSelectedTableForGuest(tableId || "");
            } else {
              setSelectedTableForGuest("");
            }
            
            setShowGuestCreate(true);
          },
        },
        {
          label: "Delete",
          onClick: () => {
            if (
              window.confirm(
                `Are you sure you want to delete guest ${guest.guest_name}?`,
              )
            ) {
              api.guests
                .deleteGuestsGuestIdGuestsGuestIdDelete(guest.guest_id)
                .then((data) => {
                  setGuests(
                    guests.filter((g) => {
                      return g.guest_id !== guest.guest_id;
                    }),
                  );
                });
            }
          },
        },
      ],
    });
  };

  const handleTableContextMenu = (e: React.MouseEvent, table: Table) => {
    e.preventDefault();

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      items: [
        {
          label: "Modify",
          onClick: () => {
            handleModifyTable(table.table_id);
          },
        },
        {
          label: "Delete",
          onClick: () => {
            if (
              window.confirm(
                `Are you sure you want to delete table ${table.table_name}?`,
              )
            ) {
              handleDeleteTable(table.table_id);
            }
          },
        },
      ],
    });
  };

  const menuRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(menuRef, () => {
    setContextMenu(undefined);
  });

  // second navbar functions
  const handleCreateTableView = () => {
    navigate(`/wedding_table/${weddingTableId}/table-layout`);
  };

  const handlePublishWedding = () => {
    navigate(`/wedding_table/${weddingTableId}/published`);
  };

  const handleGenerateQRCode = () => {
    setShowQRCode(!showQRCode);
  };

  const handleDownloadQRCode = () => {
    const canvas = document.getElementById('qrCode') as HTMLCanvasElement;
    if (canvas) {
      const link = document.createElement('a');
      link.download = 'wedding-qrcode.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    }
  };

  const DOMAIN_URL = process.env.REACT_APP_DOMAIN_URL;

  const qrcode = (
    <div className="qrcode-wrapper">
      <QRCodeCanvas
        id="qrCode"
        value={`${DOMAIN_URL}/wedding_table/${weddingTableId}/published`}
        size={300}
        bgColor={"#fff"}
        level={"H"}
      />
      <Button 
        variant="primary" 
        onClick={handleDownloadQRCode}
        className="download-qr-btn"
      >
        Download QR code
      </Button>
    </div>
  );

  const handleModifyTable = (tableId: string) => {
    const table = tables.find((t) => t.table_id === tableId);
    if (table && table.table_style) {
      const style = JSON.parse(table.table_style);
      setModifyTableID(tableId);
      setNewTableName(table.table_name);
      setNewTableCount(table.table_guest_count);
      setTableRotation(style.rotation);

      // 设置桌子样式
      const styleImage = style.imageUrl;
      const styleIndex = Object.entries(tableStyleMap).findIndex(
        ([_, url]) => url === styleImage,
      );
      setSelectedTableStyle(styleIndex + 1);

      // 设置桌子尺寸
      setTableDimensions({
        width: style.width,
        height: style.height,
      });

      setTableRotation(style.rotation);
      setNewTableStyle(table.table_style);
      setShowTableCreate(true);
    }
  };

  const handleGenerateSeatingChart = () => {
    navigate(`/wedding_table/${weddingTableId}/seating-chart`);
  };

  const handleDeleteTable = async (tableId: string) => {
    if (window.confirm("确定要删除这个桌子吗？")) {
      try {
        await api.tables.deleteTablesTableIdTablesTableIdDelete(tableId);
        const updatedTables = tables.filter(
          (table) => table.table_id !== tableId,
        );
        setTables(updatedTables);
        if (weddingTableId) {
          updateSeatingChartStyle(weddingTableId, tableId, "delete");
        }
      } catch (error) {
        console.error("删除桌子失败:", error);
      }
    }
  };

  const handleExcelUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    
    if (!file || !weddingTableId) {
      return;
    }

    // 定义中英文表头映射关系
    const headerMapping = {
      "Guest name": ["Guest name", "宾客姓名", "客人姓名", "姓名"],
      "Number of guests": ["Number of guests", "来宾人数", "宾客人数", "人数"],
      "Table name": ["Table name", "桌子名", "桌名", "桌号"],
      "Relationship with couple": ["Relationship with couple", "与新人的关系", "关系"],
      "Seats": ["Seats", "座位数", "容量", "桌子容量", "seats"],
      "Table style": ["Table style", "桌子样式", "样式"],
      "Width(cm)": ["Width(cm)", "宽度(厘米)", "宽度", "宽"],
      "Height(cm)": ["Height(cm)", "长度(厘米)", "长度", "长"]
    };
    
    // 用于获取字段值的辅助函数，支持多语言
    const getFieldValue = (obj: any, fieldKey: keyof typeof headerMapping) => {
      const possibleKeys = headerMapping[fieldKey];
      for (const key of possibleKeys) {
        if (obj[key] !== undefined) {
          return obj[key];
        }
      }
      return undefined;
    };
    
    setIsUploading(true);

    try {
      // 读取Excel文件
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);

      // 处理桌子数据
      const tablesSheet = workbook.Sheets["Tables"] || workbook.Sheets["桌子"] || workbook.Sheets["桌席"];
      if (tablesSheet) {
        const tables = XLSX.utils.sheet_to_json(tablesSheet) as Array<Record<string, any>>;
        
        // 计算每列的最大宽度和累积高度
        const columnWidths: number[] = [];
        const columnHeights: { [key: number]: number[] } = {};

        // 初始化每列的高度数组
        for (let i = 0; i < tables.length; i++) {
          const colIndex = Math.floor(i / TABLES_PER_COLUMN);
          const width = Number(getFieldValue(tables[i], "Width(cm)")) || 200;
          const height = Number(getFieldValue(tables[i], "Height(cm)")) || 200;
          
          columnWidths[colIndex] = Math.max(columnWidths[colIndex] || 0, width);
          
          if (!columnHeights[colIndex]) {
            columnHeights[colIndex] = [];
          }
          columnHeights[colIndex].push(height);
        }

        // 批量创建桌子
        for (let i = 0; i < tables.length; i++) {
          const table = tables[i];
          
          // 检查桌子名称是否有效
          const tableName = getFieldValue(table, "Table name");
          if (!tableName || String(tableName).trim() === "") {
            continue; // 跳过没有桌子名称的行
          }
          
          const col = Math.floor(i / TABLES_PER_COLUMN); // 计算列号
          const row = i % TABLES_PER_COLUMN; // 计算行号
          
          const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
          const width = Number(getFieldValue(table, "Width(cm)")) || 200;
          const height = Number(getFieldValue(table, "Height(cm)")) || 200;
          
          // 计算桌子位置（转换为厘米单位）
          const x = columnWidths.slice(0, col).reduce((sum, colWidth) => 
            sum + colWidth + TABLE_GAP / pixelsPerCm, 0);
          
          // 计算y坐标为当前列中前面所有桌子的高度之和
          const y = columnHeights[col]
            .slice(0, row)
            .reduce((sum, height) => sum + height + TABLE_GAP / pixelsPerCm, 0);
          
          const styleNumber = styleMap[getFieldValue(table, "Table style") as keyof typeof styleMap] || 1;

          const tableData = {
            table_name: String(tableName),
            table_guest_count: Number(getFieldValue(table, "Seats")) || 10,
            table_style: JSON.stringify({
              id: "",
              name: String(tableName),
              x: x,
              y: y,
              width: width,
              height: height,
              imageUrl: tableStyleMap[styleNumber],
            }),
          };

          try {
            await api.tables.postTablesWeddingTableWeddingTableIdTablesPost(
              weddingTableId,
              tableData,
            );
          } catch (error) {
            console.error("Create table error:", error);
            console.error("Failed table data:", tableData);
          }
        }
      } else {
        // 尝试查找其他可能的工作表名称
        console.log("没有找到Tables工作表，可用的工作表:", Object.keys(workbook.Sheets));
      }

      // 处理宾客数据
      const guestsSheet = workbook.Sheets["Guests"] || workbook.Sheets["宾客"] || workbook.Sheets["嘉宾"];
      if (guestsSheet) {
        // 先获取所有桌子信息
        const tablesResponse =
          await api.tables.getWeddingsWeddingTableIdTablesWeddingTableWeddingTableIdTablesGet(
            weddingTableId,
          );
        const existingTables = tablesResponse.data.contents;

        // 用于记录通过宾客信息新创建的桌子数量，用于计算位置
        let newTableCount = 0;

        const guests = XLSX.utils.sheet_to_json(guestsSheet) as Array<Record<string, any>>;

        // 批量创建宾客
        for (const guest of guests) {
          try {
            // 验证宾客数据，使用辅助函数获取值
            const guestName = getFieldValue(guest, "Guest name");
            const guestCountRaw = getFieldValue(guest, "Number of guests");
            
            if (!guestName || !guestCountRaw) {
              continue;  // 跳过无效宾客数据
            }

            // 确保宾客人数是有效的数字
            const guestCount = Number(guestCountRaw);
            if (isNaN(guestCount) || guestCount <= 0) {
              console.log("宾客人数无效，使用默认值1:", guest);
            }

            // 处理关系类型
            let relationshipType = null;
            const relationshipText = getFieldValue(guest, "Relationship with couple");
            
            
            if (relationshipText) {
              // 尝试通过label或chineseLabel匹配关系类型
              const relationshipOption = relationshipOptions.find(
                option => 
                  option.label === relationshipText || 
                  option.chineseLabel === relationshipText
              );
              
              if (relationshipOption) {
                relationshipType = relationshipOption.value;
              } else {
                console.log("警告: 未找到匹配的关系类型:", relationshipText);
                // 尝试模糊匹配（忽略大小写和额外空格）
                const fuzzyOption = relationshipOptions.find(
                  option => 
                    option.label.toLowerCase().trim() === String(relationshipText).toLowerCase().trim() ||
                    option.chineseLabel.toLowerCase().trim() === String(relationshipText).toLowerCase().trim()
                );
                
                if (fuzzyOption) {
                  relationshipType = fuzzyOption.value;
                  console.log("通过模糊匹配找到关系类型:", relationshipText, "->", relationshipType);
                }
              }
            }

            // 创建宾客数据对象
            const guestData = {
              guest_name: String(guestName),
              guest_count: isNaN(guestCount) ? 1 : guestCount,
              relationship_type: relationshipType as GuestRelationshipType,
            };
            

            // 创建宾客
            const guestResponse =
              await api.guests.postWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsPost(
                weddingTableId,
                guestData
              );
            
            // 如果指定了桌子名称，处理桌子分配逻辑
            const tableName = getFieldValue(guest, "Table name");
            if (tableName && String(tableName).trim() !== "") {
              let targetTable = existingTables.find(
                (table) => table.table_name === String(tableName).trim(),
              );

              // 如果桌子不存在，创建新桌子
              if (!targetTable) {
                // 计算新桌子的位置
                const TABLES_PER_ROW = 4; // 每行最多放4张桌子
                const TABLE_SPACING = 100; // 桌子间的间距(像素)
                const pixelsPerCm = GRID_SIZE / REAL_GRID_SIZE;
                
                const row = Math.floor(newTableCount / TABLES_PER_ROW);
                const col = newTableCount % TABLES_PER_ROW;
                
                const x = (col * TABLE_SPACING) / pixelsPerCm; // 转换为厘米
                const y = (row * TABLE_SPACING) / pixelsPerCm; // 转换为厘米

                // 使用Excel的seats字段作为桌子容量，如果未提供则使用默认值10
                let tableCapacity = 10;
                const seats = getFieldValue(guest, "Seats");
                if (seats) {
                  const parsedCapacity = Number(seats);
                  if (!isNaN(parsedCapacity) && parsedCapacity > 0) {
                    tableCapacity = parsedCapacity;
                  }
                }

                const tableData = {
                  table_name: String(tableName),
                  table_guest_count: tableCapacity,
                  table_style: JSON.stringify({
                    id: "",
                    name: String(tableName),
                    x: x,
                    y: y,
                    width: 200,
                    height: 200,
                    imageUrl: tableStyleMap[1], // 默认圆桌
                  }),
                };

                try {
                  const newTableResponse =
                    await api.tables.postTablesWeddingTableWeddingTableIdTablesPost(
                      weddingTableId,
                      tableData,
                    );
                  targetTable = newTableResponse.data;
                  existingTables.push(targetTable);
                  newTableCount++; // 增加新创建的桌子计数
                } catch (tableError) {
                  console.error("创建桌子失败:", tableError);
                  continue; // 如果创建桌子失败，跳过分配宾客
                }
              }

              // 分配宾客到桌子
              try {
                await api.guests.patchGuestsGuestIdGuestsGuestIdPatch(
                  guestResponse.data.guest_id,
                  {
                    table_id: targetTable.table_id,
                  },
                );
              } catch (assignError) {
                console.error("分配宾客到桌子失败:", assignError);
              }
            }
          } catch (error) {
            console.error("处理宾客数据失败:", error, "问题数据:", guest);
            // 继续处理下一个宾客
          }
        }
      } else {
        // 尝试其他可能的工作表名称
        const possibleSheetNames = ["Guests", "宾客", "嘉宾", "客人", "Sheet1"];
        let foundSheet = false;
        
        for (const sheetName of possibleSheetNames) {
          if (workbook.Sheets[sheetName]) {
            // 这里可以添加处理代码或者重用上面的代码
            foundSheet = true;
            break;
          }
        }
        
        if (!foundSheet) {
          console.error("Excel文件中没有找到宾客工作表! 可用的工作表:", Object.keys(workbook.Sheets));
          alert("Excel文件中没有找到宾客工作表，请确保文件包含名为'Guests'或'宾客'的工作表");
        }
      }

      // 刷新数据
      const guestsResponse =
        await api.guests.getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(
          weddingTableId,
        );
      setGuests(guestsResponse.data.contents.filter((guest) => !guest.table));

      const tablesResponse =
        await api.tables.getWeddingsWeddingTableIdTablesWeddingTableWeddingTableIdTablesGet(
          weddingTableId,
        );
      setTables(tablesResponse.data.contents);
    } catch (error) {
      console.error("导入失败:", error);
      console.error("错误详情:", error instanceof Error ? error.message : String(error));
      alert("导入失败，请检查Excel文件格式是否正确");
    } finally {
      setIsUploading(false);
      // 清空input的value，这样同一个文件可以重复上传
      if (event.target) {
        event.target.value = "";
      }
    }
  };

  const handleClearAllTables = async () => {
    if (window.confirm("确定要清空所有桌子吗？此操作无法撤销！")) {
      try {
        // 显示加载状态
        setIsUploading(true);
        
        // 复制一份桌子数组以便遍历
        const tablesToDelete = [...tables];
        
        // 逐个删除桌子
        for (const table of tablesToDelete) {
          await api.tables.deleteTablesTableIdTablesTableIdDelete(table.table_id);
          if (weddingTableId) {
            await updateSeatingChartStyle(weddingTableId, table.table_id, "delete");
          }
        }
        
        // 清空桌子数组
        setTables([]);
        
        // 重新获取宾客列表（未分配到桌子的宾客）
        if (weddingTableId) {
          const guestsResponse = 
            await api.guests.getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(weddingTableId);
          setGuests(guestsResponse.data.contents.filter((guest) => !guest.table));
        }
        
        // 显示成功提示
        alert("所有桌子已成功清空");
      } catch (error) {
        console.error("清空失败:", error);
        alert("清空操作失败，请稍后重试");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleClearAllGuests = async () => {
    if (window.confirm("确定要删除所有宾客吗？此操作无法撤销！")) {
      try {
        // 显示加载状态
        setIsUploading(true);
        
        // 获取所有宾客（包括已分配到桌子的宾客）
        if (weddingTableId) {
          const allGuestsResponse = 
            await api.guests.getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(
              weddingTableId
            );
          
          const allGuests = allGuestsResponse.data.contents;
          
          // 逐个删除宾客
          for (const guest of allGuests) {
            await api.guests.deleteGuestsGuestIdGuestsGuestIdDelete(guest.guest_id);
          }
          
          // 清空宾客数组
          setGuests([]);
          
          // 更新桌子（移除其中的宾客）
          const tablesResponse = 
            await api.tables.getWeddingsWeddingTableIdTablesWeddingTableWeddingTableIdTablesGet(
              weddingTableId
            );
          setTables(tablesResponse.data.contents);
          
          // 显示成功提示
          alert("所有宾客已成功删除");
        }
      } catch (error) {
        console.error("删除宾客失败:", error);
        alert("删除宾客操作失败，请稍后重试");
      } finally {
        setIsUploading(false);
      }
    }
  };

  // 添加排序切换函数
  const toggleGuestSort = () => {
    setGuestSortOrder((prev) => {
      if (prev === "none") return "asc";
      if (prev === "asc") return "desc";
      return "none";
    });
  };

  const toggleTableSort = () => {
    setTableSortOrder((prev) => {
      if (prev === "none") return "asc";
      if (prev === "asc") return "desc";
      return "none";
    });
  };

  // 根据关系类型过滤宾客
  const filteredGuests = useMemo(() => {
    if (selectedRelationship === "all") return guests;
    return guests.filter(
      (guest) => guest.relationship_type === selectedRelationship,
    );
  }, [guests, selectedRelationship]);

  // 对过滤后的宾客进行排序
  const sortedGuests = useMemo(() => {
    if (guestSortOrder === "none") return filteredGuests;

    return [...filteredGuests].sort((a, b) => {
      const nameA = a.guest_name?.toLowerCase() || "";
      const nameB = b.guest_name?.toLowerCase() || "";

      if (guestSortOrder === "asc") {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
  }, [filteredGuests, guestSortOrder]);

  const sortedTables = useMemo(() => {
    if (tableSortOrder === "none") return tables;

    return [...tables].sort((a, b) => {
      const nameA = a.table_name?.toLowerCase() || "";
      const nameB = b.table_name?.toLowerCase() || "";

      if (tableSortOrder === "asc") {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
  }, [tables, tableSortOrder]);

  // 修改函数签名接受更宽松的参数类型
  const isDuplicateName = (guestName: string, allGuests: {guest_name?: string}[]): boolean => {
    return allGuests.filter(g => g.guest_name === guestName).length > 1;
  };

  const handleScrapeInvitationLink = async () => {
    if (!invitationLink || !weddingTableId) return;
    
    try {
      setIsScrapingGuests(true);
      
      // 调用后端API进行爬虫操作
      const response = await api.guests.scrapeInvitationLinkGuestsWeddingTableIdScrapeLinkPost(
        weddingTableId
      );
      
      if (response.data && response.data.guests) {
        // 计算成功导入的宾客数量
        const importedGuests = response.data.guests;
        const successCount = importedGuests.length;
        
        // 刷新宾客列表
        const guestsResponse = await api.guests.getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(
          weddingTableId
        );
        setGuests(guestsResponse.data.contents.filter((guest) => !guest.table));
        
        // 提示用户导入成功
        alert(`成功从电子请帖导入了 ${successCount} 位宾客`);
        setShowInvitationLinkModal(false);
        setInvitationLink("");
      }
    } catch (error) {
      console.error("从电子请帖导入宾客失败:", error);
      alert("导入失败，请检查链接是否正确或稍后再试");
    } finally {
      setIsScrapingGuests(false);
    }
  };

  // 处理请帖链接保存和导入
  const handleSaveInvitationLink = () => {
    if (!invitationLink) {
      alert("请输入有效的电子请帖链接");
      return;
    }
    
    if (!weddingTableId) {
      alert("婚宴表ID无效");
      return;
    }
    
    // 准备要更新的数据 - 使用一个更完整的对象
    const currentExtras = { ...(weddingTable?.extras || {}) };
    
    // 更新invitation_link，同时添加时间戳避免缓存问题
    currentExtras.invitation_link = invitationLink;
    currentExtras._timestamp = new Date().getTime(); // 添加时间戳确保变化被识别
    
    // 如果是修改链接，则清空last_scraped_time
    if (hasExistingLink) {
      delete currentExtras.last_scraped_time;
    }
    
    setIsLoading(true);
    
    // 使用完整的wedding_table对象更新
    const updateData = {
      extras: currentExtras,
      wedding_table_id: weddingTableId
    };
    
    // 调用API更新wedding_table
    api.weddings
      .patchWeddingsWeddingTableIdWeddingTableWeddingTableIdPatch(
        weddingTableId as string,
        updateData
      )
      .then((data) => {
        console.log("API返回成功:", data.data);
        
        // 手动更新本地状态，确保UI反映最新值
        const updatedWeddingTable = {
          ...data.data,
          extras: {
            ...data.data.extras,
            invitation_link: invitationLink // 使用本地值确保显示正确
          }
        };
        
        // 即使API返回的数据没有更新，也强制使用我们的新值
        setWeddingTable(updatedWeddingTable);
        setHasExistingLink(true);
        setIsEditingLink(false);
        
        // 增加延迟，确保数据库更新完成
        setTimeout(() => {
          // 保存成功后开始导入宾客数据前先再次尝试获取最新数据
          api.weddings.getWeddingWeddingTableWeddingTableIdGet(weddingTableId as string)
            .then(refreshData => {
              setWeddingTable(refreshData.data);
              handleImportGuests();
            })
            .catch(err => {
              console.error("刷新数据失败，继续使用本地数据:", err);
              handleImportGuests();
            });
        }, 1000);
      })
      .catch((error) => {
        console.error("保存电子请帖链接失败:", error.response?.data || error);
        alert("保存电子请帖链接失败，请重试");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  
  // 开始编辑链接
  const handleEditLink = () => {
    setIsEditingLink(true);
  };
  
  // 取消编辑链接
  const handleCancelEditLink = () => {
    if (hasExistingLink) {
      setInvitationLink(weddingTable?.extras?.invitation_link || "");
      setIsEditingLink(false);
    }
  };
  
  // 导入宾客数据
  const handleImportGuests = () => {
    setIsLoading(true);
    api.guests.scrapeInvitationLinkGuestsWeddingTableIdScrapeLinkPost(weddingTableId as string)
      .then((data) => {
        if (data.data.success) {
          // 刷新宾客列表
          api.guests.getWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsGet(
            weddingTableId as string
          ).then(response => {
            setGuests(response.data.contents.filter((guest) => !guest.table));
            
            // 同时刷新桌子数据
            api.tables.getWeddingsWeddingTableIdTablesWeddingTableWeddingTableIdTablesGet(
              weddingTableId as string
            ).then(tablesResponse => {
              setTables(tablesResponse.data.contents);
            }).catch(error => {
              console.error("刷新桌子数据失败:", error);
            });
            
            alert(`成功导入 ${data.data.guests.length} 位新宾客`);
          });
        } else {
          alert(data.data.message || "导入宾客数据失败");
        }
      })
      .catch((error) => {
        console.error("导入宾客数据失败:", error);
        alert("导入宾客数据失败，请重试");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  
  // 渲染电子请帖链接部分
  const renderInvitationLinkSection = () => {
    return (
      <div className="invitation-link-section">
        <h4>电子请帖集成</h4>
        
        {hasExistingLink && !isEditingLink ? (
          // 显示已有的链接和修改按钮
          <div className="existing-link-container">
            <div className="form-group mb-3">
              <label>当前电子请帖链接：</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  value={invitationLink}
                  disabled
                />
                <button 
                  className="btn btn-outline-secondary" 
                  type="button"
                  onClick={handleEditLink}
                >
                  修改电子请帖链接
                </button>
              </div>
            </div>
            <button 
              className="btn btn-primary"
              onClick={handleImportGuests}
              disabled={isLoading}
            >
              {isLoading ? "导入中..." : "导入宾客数据"}
            </button>
          </div>
        ) : (
          // 显示编辑模式
          <div className="edit-link-container">
            <div className="form-group mb-3">
              <label>{hasExistingLink ? "修改电子请帖链接：" : "请输入电子请帖链接："}</label>
              <input
                type="text"
                className="form-control"
                placeholder="例如：https://h5.hunlihu1.com/vit/RKKKefRK3K"
                value={invitationLink}
                onChange={(e) => setInvitationLink(e.target.value)}
              />
              {hasExistingLink && (
                <small className="text-danger">
                  注意：修改链接后将清除之前的导入记录，可能导致宾客数据重复导入
                </small>
              )}
            </div>
            <div className="button-group">
              <button 
                className="btn btn-primary"
                onClick={handleSaveInvitationLink}
                disabled={isLoading}
              >
                {isLoading ? "保存中..." : (hasExistingLink ? "保存并导入" : "开始导入")}
              </button>
              {hasExistingLink && (
                <button 
                  className="btn btn-secondary ms-2"
                  onClick={handleCancelEditLink}
                >
                  取消
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // 添加检测窗口大小的useEffect
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // 初始检查
    checkIsMobile();
    
    // 添加窗口大小变化监听
    window.addEventListener('resize', checkIsMobile);
    
    // 清理监听器
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // 添加一个辅助函数，计算桌子上的总人数
  const totalGuests = (guests: any[]) => {
    let sum = 0;
    guests.forEach((guest) => {
      sum += guest.guest_count || 0;
    });
    return sum;
  };

  return (
    <div
      className="wedding-page"
      onContextMenu={(e) => {
        e.preventDefault(); // prevent the default behaviour when right clicked
      }}
    >
      <Navbar className="secondary-navbar" expand="lg">
        <div className="d-flex justify-content-center w-100">
          {!isMobile && (
            <>
              <Button variant="outline-primary" onClick={handleCreateTableView}>
                Modify Table View
              </Button>
              <Button variant="outline-primary" onClick={handlePublishWedding}>
                Publish My Wedding
              </Button>
              <Button
                variant="outline-primary"
                onClick={handleGenerateSeatingChart}
              >
                Generate Seating Chart
              </Button>
              <Button variant="outline-primary" onClick={handleGenerateQRCode}>
                Generate QR code
              </Button>
            </>
          )}
          <Button
            variant="outline-primary"
            as="label"
            style={{ position: "relative" }}
            disabled={isUploading}
          >
            {isUploading ? "Importing..." : "Excel Batch Import"}
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                opacity: 0,
                width: "100%",
                height: "100%",
                cursor: "pointer",
              }}
            />
          </Button>
          {!isMobile && (
            <>
              <Button
                variant="outline-danger"
                onClick={handleClearAllTables}
                disabled={isUploading || tables.length === 0}
              >
                Clear All Tables
              </Button>
              <Button
                variant="outline-danger"
                onClick={handleClearAllGuests}
                disabled={isUploading || guests.length === 0}
              >
                Clear All Guests
              </Button>
            </>
          )}
          <Button 
            variant="outline-primary" 
            onClick={() => setShowInvitationLinkModal(true)}
          >
            Invitation Link
          </Button>
          {showQRCode && (
            <div className="qrcode-container">
              <div>{qrcode}</div>
              <button className="close-btn" onClick={handleGenerateQRCode}>
                x
              </button>
            </div>
          )}
        </div>
      </Navbar>
      <DndProvider backend={HTML5Backend}>
        <div className="table-container">
          <div className="list-section">
            <div className="guest-list-container">
              <Box sx={{ display: "flex", height: "100%" }}>
                <Tabs
                  orientation="vertical"
                  variant="scrollable"
                  value={selectedRelationship}
                  onChange={(_, newValue) => setSelectedRelationship(newValue)}
                  aria-label="Relationship Type Filter"
                  sx={{
                    borderRight: 1,
                    borderColor: "divider",
                    minWidth: "120px",
                  }}
                >
                  <Tab label="All Guests" value="all" />
                  {relationshipOptions.map((option) => (
                    <Tab
                      key={option.value}
                      label={option.label}
                      value={option.value}
                    />
                  ))}
                </Tabs>
                <div
                  className="guest-list"
                  style={{ flex: 1, overflow: "auto" }}
                >
                  <nav className="list-header">
                    <div className="header-title-container">
                      <div className="header-title">Guests</div>
                      <div className="sort-button" onClick={toggleGuestSort}>
                        {guestSortOrder === "none" && "sort"}
                        {guestSortOrder === "asc" && "↑"}
                        {guestSortOrder === "desc" && "↓"}
                      </div>
                    </div>
                    <div
                      className="add-btn"
                      onClick={() => setShowGuestCreate(true)}
                    />
                  </nav>
                  {sortedGuests.map((guest) => (
                    <DraggableGuest
                      key={guest.guest_id}
                      guest={guest}
                      onContextMenu={handleGuestContextMenu}
                      onClick={isMobile ? handleGuestContextMenu : undefined}
                      isDuplicate={isDuplicateName(guest.guest_name || "", [...guests, ...tables.flatMap(t => t.guests || [])])}
                      isMobile={isMobile}
                    />
                  ))}
                  {sortedGuests.length === 0 && (
                    <div className="list-item">No guests yet</div>
                  )}
                </div>
              </Box>
            </div>
          </div>

          <div className="list-section">
            <div className="table-list">
              <nav className="list-header">
                <div className="header-title-container">
                  <div className="header-title">Tables</div>
                  <div className="sort-button" onClick={toggleTableSort}>
                    {tableSortOrder === "none" && "sort"}
                    {tableSortOrder === "asc" && "↑"}
                    {tableSortOrder === "desc" && "↓"}
                  </div>
                </div>
                <div className="add-btn" onClick={handleAddTableClick} />
              </nav>
              {sortedTables.map((table) => (
                <DroppableTable
                  key={table.table_id}
                  table={table}
                  handleDropGuest={handleGuestAddToTable}
                  handleRemoveGuest={handleRemoveTableGuest}
                  onContextMenu={handleTableContextMenu}
                  onClick={isMobile ? handleTableContextMenu : undefined}
                  isDuplicateName={isDuplicateName}
                  allGuests={[...guests, ...tables.flatMap(t => t.guests || [])]}
                  isMobile={isMobile}
                />
              ))}
              {sortedTables.length === 0 && (
                <div className="list-item">No tables yet</div>
              )}
            </div>
          </div>
        </div>
      </DndProvider>
      <Modal show={showGuestCreate} onHide={handleCancelModal}>
        <Modal.Header closeButton>
          <Modal.Title>{modifyGuestID ? "Modify Guest" : "Add Guest"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="form-group mb-3">
            <label>Guest Name:</label>
            <input
              type="text"
              className="form-control"
              value={newGuestName || ""}
              onChange={(e) => setNewGuestName(e.target.value)}
            />
          </div>
          <div className="form-group mb-3">
            <label>Number of people:</label>
            <input
              type="number"
              className="form-control"
              value={newGuestCount || ""}
              onChange={(e) => setNewGuestCount(Number(e.target.value))}
            />
          </div>
          <div className="form-group mb-3">
            <label>Relationship:</label>
            <select
              className={`form-control ${showValidationError && !guestRelationship ? "is-invalid" : ""}`}
              value={guestRelationship}
              onChange={(e) => setGuestRelationship(e.target.value)}
            >
              <option value="">Please select relationship type</option>
              {relationshipOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {showValidationError && !guestRelationship && (
              <div className="invalid-feedback">Please select relationship type</div>
            )}
          </div>
          
          {/* 移动端专用的桌子选择框 */}
          {isMobile && (
            <div className="form-group mb-3">
              <label>Assign to table:</label>
              <select
                className="form-control"
                value={selectedTableForGuest}
                onChange={(e) => setSelectedTableForGuest(e.target.value)}
              >
                <option value="">Not assign to table</option>
                {tables.map((table) => (
                  <option key={table.table_id} value={table.table_id}>
                    {table.table_name} ({totalGuests(table.guests || [])}/{table.table_guest_count})
                  </option>
                ))}
              </select>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelModal}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleGuestCreate}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showTableCreate} onHide={handleCancelModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {modifyTableID ? "Modify Table" : "Add Table"}
          </Modal.Title>
        </Modal.Header>
        <input
          type="text"
          placeholder="Enter table name"
          value={newTableName}
          onChange={(e) => setNewTableName(e.target.value)}
        />
        <input
          type="number"
          placeholder="Enter number of guests can sit"
          value={newTableCount || ""}
          onChange={(e) => setNewTableCount(parseInt(e.target.value))}
        />
        <div className="table-style-images">
          {[1, 2, 3, 4, 5, 6, 7].map((index) => (
            <div
              key={index}
              className={`table-style-image-container${index === selectedTableStyle ? " selected" : ""}`}
              onClick={() => {
                setSelectedTableStyle(index);
                setTableDimensions({
                  ...tableDimensions,
                  width: index === 1 ? 200 : 
                         index === 2 ? 200 : 
                         index === 3 ? 400 : 
                         index === 4 ? 200 : 
                         index === 5 ? 200 : 
                         index === 6 ? 200 : 
                         index === 7 ? 100 : 200,
                  height: index === 1 ? 200 : 
                          index === 2 ? 200 : 
                          index === 3 ? 200 : 
                          index === 4 ? 200 : 
                          index === 5 ? 200 : 
                          index === 6 ? 800 : 
                          index === 7 ? 200 : 200,
                });
              }}
            >
              <img
                src={tableStyleMap[index as keyof typeof tableStyleMap]}
                alt={`Table Style ${index}`}
                style={{
                  maxWidth: "100%",
                  maxHeight: "60px",
                  objectFit: "contain"
                }}
              />
              <span>Table Style {index}</span>
            </div>
          ))}
        </div>
        {renderDimensionInputs()}
        {renderRotationInput()}
        <div className="modal-buttons">
          <button className="save-btn" onClick={handleTableCreate}>
            {modifyTableID ? "Modify" : "Save"}
          </button>
          <button className="cancel-btn" onClick={handleCancelModal}>
            Cancel
          </button>
        </div>
      </Modal>
      {contextMenu && (
        <div ref={menuRef}>
          <ContextMenu
            x={contextMenu.x}
            y={contextMenu.y}
            items={contextMenu.items}
          />
        </div>
      )}
      <Modal 
        show={showDuplicateConfirm} 
        onHide={() => setShowDuplicateConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="fas fa-exclamation-triangle" style={{ color: '#ff9800', marginRight: '10px' }}></i>
            Duplicate Guest Name
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="duplicate-warning">
            <div className="existing-guest-info">
              <h5>Existing Guests with the same name:</h5>
              {duplicateGuests.map((guest, index) => (
                <div key={guest.guest_id} className="guest-detail" style={{marginBottom: '10px'}}>
                  <div><strong>Name:</strong> {guest.guest_name}</div>
                  <div><strong>Number of people:</strong> {guest.guest_count}</div>
                  <div>
                    <strong>Relationship:</strong> {
                      relationshipOptions.find(option => option.value === guest.relationship_type)?.label || 
                      guest.relationship_type
                    }
                  </div>
                  {guest.table && (
                    <div><strong>Table:</strong> {
                      tables.find(t => t.table_id === guest.table_id)?.table_name || String(guest.table_id)
                    }</div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="new-guest-info" style={{ marginTop: '15px' }}>
              <h5>You are trying to add:</h5>
              <div className="guest-detail">
                <div><strong>Name:</strong> {newGuestName}</div>
                <div><strong>Number of people:</strong> {newGuestCount}</div>
                <div>
                  <strong>Relationship:</strong> {
                    relationshipOptions.find(option => option.value === guestRelationship)?.label || 
                    guestRelationship
                  }
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDuplicateConfirm(false)}
          >
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              setShowDuplicateConfirm(false);
              // 继续创建宾客
              if (weddingTableId) {
                api.guests
                  .postWeddingsWeddingTableIdGuestsWeddingTableWeddingTableIdGuestsPost(weddingTableId, {
                    guest_name: newGuestName || "",
                    guest_count: newGuestCount,
                    relationship_type: guestRelationship as GuestRelationshipType,
                  })
                  .then((data) => {
                    setGuests([...guests, data.data]);
                    handleCancelModal();
                  })
                  .catch((error) => {
                    console.error("创建宾客失败:", error);
                    alert("创建宾客失败，请重试");
                  });
              }
            }}
          >
            Add Anyway
          </Button>
        </Modal.Footer>
      </Modal>
      <Modal show={showInvitationLinkModal} onHide={() => setShowInvitationLinkModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>导入电子请帖宾客</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {renderInvitationLinkSection()}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default WeddingTableListPage;
